// src/services/llmService.ts
import { supabase } from "@/integrations/supabase/client";
import { modelConfigService } from "./modelConfigService";

interface LLMResponse {
  success: boolean;
  content?: string;
  error?: string;
  model_used?: string;
}

// Options interface for better maintainability
export interface ChatOptions {
  prompt: string;
  history?: Array<{ role: string; content: string }>;
  model?: string;
  role?: string | null;
  attachments?: Array<{ name: string; type: string; content: string; size: number; metadata?: any }>;
}

/**
 * Sends a prompt and history to the backend LLM function, defaulting to Llama 4 via Novita.
 * Supports both legacy positional arguments and modern options object.
 */
export async function generateChatResponse(options: ChatOptions): Promise<string>;
export async function generateChatResponse(
  prompt: string,
  history?: Array<{ role: string; content: string }>,
  model?: string,
  activeRole?: string | null,
  attachments?: Array<{ name: string; type: string; content: string; size: number; metadata?: any }>
): Promise<string>;
export async function generateChatResponse(
  promptOrOptions: string | ChatOptions,
  history: Array<{ role: string; content: string }> = [],
  model: string = 'auto',
  activeRole?: string | null,
  attachments?: Array<{ name: string; type: string; content: string; size: number; metadata?: any }>
): Promise<string> {
  // Get dynamic model configuration from admin settings
  let modelConfig;
  try {
    modelConfig = await modelConfigService.getCurrentConfig();
  } catch (error) {
    console.warn('Failed to load model config, using defaults:', error);
    modelConfig = {
      modelId: 'meta-llama/llama-4-maverick-17b-128e-instruct-fp8',
      apiKey: '', // Never hardcode API keys - use env vars only
      displayName: 'Llama 4',
      badge: 'Professional',
      temperature: 0.2
    };
  }

  // Handle both options object and legacy positional arguments
  const options: ChatOptions = typeof promptOrOptions === 'string' 
    ? { prompt: promptOrOptions, history, model, role: activeRole, attachments }
    : promptOrOptions;
  
  const { 
    prompt, 
    history: chatHistory = [], 
    model: chatModel = modelConfig.modelId,
    role,
    attachments: chatAttachments
  } = options;
  console.log(`Generating AI response for: ${prompt.substring(0, 50)}${prompt.length > 50 ? '...' : ''}`);
  console.log(`With history entries: ${chatHistory.length}`);
  console.log(`Using role: ${role || 'default'}`);
  console.log(`With attachments: ${chatAttachments?.length || 0}`);

  // Process attachments and add to prompt if provided
  let enhancedPrompt = prompt;
  let hasImageData = false;
  if (chatAttachments && chatAttachments.length > 0) {
    const attachmentContext = chatAttachments.map(file => {
      const metadata = file.metadata ? 
        ` (${file.metadata.wordCount ? `${file.metadata.wordCount} words` : ''}${file.metadata.sheets ? `, ${file.metadata.sheets.length} sheets` : ''}${file.metadata.mimeType ? `, ${file.metadata.mimeType}` : ''})` : '';
      
      // Handle image data specially for vision models
      if (file.content.startsWith('[IMAGE_DATA:')) {
        hasImageData = true;
        return `\n\n--- UPLOADED IMAGE: ${file.name}${metadata} ---\nI can see you've uploaded an image. Let me analyze its visual content for you.\n--- END OF IMAGE ---`;
      } else {
        // Truncate large file contents to prevent token limit issues
        const MAX_CONTENT_LENGTH = 8000; // ~2000 tokens worth of content
        let content = file.content;
        if (content.length > MAX_CONTENT_LENGTH) {
          content = content.substring(0, MAX_CONTENT_LENGTH) + '\n\n[Content truncated due to length - showing first 8000 characters]';
        }
        return `\n\n--- UPLOADED FILE: ${file.name}${metadata} ---\n${content}\n--- END OF FILE ---`;
      }
    }).join('');
    
    const visionInstructions = hasImageData 
      ? '\n\nIMPORTANT: You are a multimodal AI with vision capabilities. When users upload images, you CAN and SHOULD analyze them directly. Describe what you see in detail and provide relevant insights based on the visual content.'
      : '';
    enhancedPrompt = `${prompt}\n\nUploaded files for analysis:${attachmentContext}${visionInstructions}`;
    console.log(`Enhanced prompt with ${chatAttachments.length} file(s)`);
  }

  // Create role-specific system prompts
  const getRoleSystemPrompt = (role: string | null | undefined): string => {
    switch (role) {
      case 'analyst':
        return 'You are Stewie Trader\'s Financial Analyst AI, a specialized expert in market analysis, portfolio optimization, and investment strategies. Provide detailed financial analysis, market insights, and data-driven recommendations. Use technical and fundamental analysis principles. Always note that your recommendations are educational and not personalized financial advice.';
      
      case 'compliance':
        return 'You are Stewie Trader\'s Compliance Manager AI, an expert in financial regulations, risk management, and regulatory oversight. Focus on compliance requirements, regulatory guidelines, risk assessment, and best practices in financial services. Provide educational guidance on regulatory matters without giving legal advice.';
      
      case 'concierge':
        return 'You are Stewie Trader\'s E-Concierge AI, a personalized support assistant focused on user experience, guidance, and general financial education. Provide helpful, friendly, and accessible explanations of financial concepts. Guide users through platform features and offer general investment education.';
      
      default: {
        const hasVisionSupport = chatModel.includes('vision') || chatModel.includes('multimodal') || hasImageData;
        return `You are a professional financial assistant${hasVisionSupport ? ' with multimodal capabilities' : ''}, specialized in portfolio management, market analysis, and investment strategies. You can analyze text documents, spreadsheets${hasVisionSupport ? ', images,' : ''} and other uploaded content. When users upload files, analyze their content directly and provide relevant insights. Maintain a professional, helpful tone and always note that your recommendations are educational and not personalized financial advice.`;
      }
    }
  };

  try {
    const { data, error } = await supabase.functions.invoke<LLMResponse>(
      'generate-chat-response',
      {
        body: { 
          prompt: enhancedPrompt, 
          history: chatHistory, 
          model: chatModel,
          systemPrompt: getRoleSystemPrompt(role), // Pass role-specific system prompt
          hasImageData,
          attachments: hasImageData ? chatAttachments : undefined, // Pass raw attachments for vision processing
          temperature: modelConfig.temperature // Use admin-configured temperature
        },
      }
    );

    if (error) {
      console.error("Edge function invocation error:", error);
      throw new Error(error.message || "Failed to invoke LLM function.");
    }

    if (!data?.success || !data.content) {
      console.error("Edge function returned failure or no content:", data);
      throw new Error(data?.error || "LLM function returned an error or no content.");
    }

    // Log model used if provided
    if (data.model_used) {
      console.log(`Response generated using model: ${data.model_used} with role: ${role || 'default'}`);
    }

    return data.content;

  } catch (err) {
    console.error("Error calling generateChatResponse:", err);
    // Re-throw a more user-friendly error
    if (err instanceof Error) {
       throw new Error(`Failed to get AI response: ${err.message}`);
    } else {
       throw new Error("An unknown error occurred while contacting the AI.");
    }
  }
}

// You can add more specific functions here later, e.g., for financial analysis
